import { createRouter, createWebHistory } from 'vue-router'
import Layout from '@/views/Layout.vue'
import Login from '@/views/Login.vue'
import Dashboard from '@/views/Dashboard.vue'
import BasicData from '@/views/BasicData.vue'
import BrandManagement from '@/views/BrandManagement.vue'
import ColorManagement from '@/views/ColorManagement.vue'
import Customers from '@/views/Customers.vue'
import PermissionsModule from '@/views/permissions/Index.vue'
import ChangePassword from '@/views/ChangePassword.vue'
import { ElMessage } from 'element-plus'
import { hasPermission, canAccessFinanceModuleKey, canViewBillingStatements, canManageUsersAndRoles } from '@/utils/permission'
import { forceAuthRelogin, shouldLogoutForIdle, markUserActive } from '@/utils/auth'
import { useUserStore } from '@/store/user'
import { getFirstAllowedMobilePath, canAccessMobilePath, canAccessDesktopMobileWorkbench } from '@/utils/mobileAccess'
import { MOBILE_DESKTOP_EMBED_BASE } from '@/utils/mobileWorkbenchBase'
import { getMobileWorkbenchChildren } from './mobileWorkbenchChildren'
import { isLikelyMobileBrowser } from '@/utils/device'
import { resetBodyElLoadingArtifacts } from '@/utils/elementLoadingCleanup'
import WarehouseLayout from '@/views/inventory/WarehouseLayout.vue'
import DispatchOrderManagement from '@/views/orders/DispatchOrderManagement.vue'
const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login
  },
  {
    path: '/m',
    component: () => import('@/views/mobile/MobileLayout.vue'),
    meta: { requiresAuth: true, embeddedInDesktop: false },
    children: getMobileWorkbenchChildren({ basePath: '/m', namePrefix: '' })
  },
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: Dashboard,
        meta: { title: '仪表盘', requiresAuth: true }
      },
      {
        path: 'change-password',
        name: 'ChangePassword',
        component: ChangePassword,
        meta: { title: '修改密码', requiresAuth: true }
      },
      {
        path: 'basic-data',
        name: 'BasicData',
        component: BasicData,
        meta: { title: '基础资料', requiresAuth: true }
      },
      {
        path: 'brands',
        name: 'BrandManagement',
        component: BrandManagement,
        meta: { title: '品牌管理', requiresAuth: true, permission: 'customers.brands.view' }
      },
      {
        path: 'colors',
        name: 'ColorManagement',
        component: ColorManagement,
        meta: { title: '颜色管理', requiresAuth: true, permission: 'masterdata.colors.view' }
      },
      {
        path: 'customers',
        name: 'Customers',
        component: Customers,
        meta: { title: '客户管理', requiresAuth: true, permission: 'customers.records.view' }
      },
      {
        path: 'suppliers',
        name: 'Suppliers',
        component: () => import('@/views/Suppliers.vue'),
        meta: { title: '供应商管理', requiresAuth: true, permission: 'purchase.suppliers.view' }
      },
      // 订单管理相关路由
      {
        path: 'orders',
        name: 'Orders',
        component: () => import('@/views/Orders.vue'),
        meta: { title: '销售订单', requiresAuth: true, permission: 'orders.sales.view' }
      },
      {
        path: 'admin/order-item-client-material-name-sync',
        name: 'OrderItemClientMaterialNameSync',
        component: () => import('@/views/admin/OrderItemClientMaterialNameSync.vue'),
        meta: {
          title: '客户产品名称同步',
          requiresAuth: true,
          permission: 'global.admin'
        }
      },
      {
        path: 'admin/matching-batch-correction',
        name: 'AdminMatchingBatchCorrection',
        component: () => import('@/views/admin/MatchingBatchCorrection.vue'),
        meta: {
          title: '配料料号纠正',
          requiresAuth: true,
          permission: 'global.admin'
        }
      },
      {
        path: 'inventory/batch-identity-correction',
        name: 'AdminBatchIdentityCorrection',
        component: () => import('@/views/admin/BatchIdentityCorrection.vue'),
        meta: {
          title: '料号身份纠错',
          requiresAuth: true,
          permissionAny: [
            'inventory.material_batches.identity_correction',
            'global.admin'
          ]
        }
      },
      {
        path: 'admin/dispatch-site-capacity',
        name: 'DispatchSiteCapacitySettings',
        component: () => import('@/views/admin/DispatchSiteCapacitySettings.vue'),
        meta: {
          title: '现场派工产能',
          requiresAuth: true,
          permission: 'global.admin'
        }
      },
      {
        path: 'orders/material-matching',
        name: 'MaterialMatchingManagement',
        component: () => import('@/views/orders/MaterialMatchingManagement.vue'),
        meta: { title: '配料管理', requiresAuth: true, permission: 'orders.material_matching.view' }
      },
      {
        path: 'orders/material-matching-query',
        name: 'MaterialMatchingQuery',
        component: () => import('@/views/orders/MaterialMatchingQuery.vue'),
        meta: { title: '配料查询', requiresAuth: true, permission: 'orders.material_matching.view' }
      },
      {
        path: 'orders/item-tracking',
        name: 'OrderItemTracking',
        component: () => import('@/views/orders/OrderItemTracking.vue'),
        meta: {
          title: '订单明细追踪',
          requiresAuth: true,
          permissionAny: [
            'orders.sales.view',
            'orders.material_matching.view',
            'orders.dispatch.view',
            'orders.delivery.view'
          ]
        }
      },
      {
        path: 'orders/order-summary',
        name: 'OrderSummaryMatrix',
        component: () => import('@/views/orders/OrderSummaryMatrix.vue'),
        meta: {
          title: '订单汇总表',
          requiresAuth: true,
          permissionAny: [
            'orders.sales.view',
            'orders.material_matching.view',
            'orders.dispatch.view',
            'orders.delivery.view'
          ]
        }
      },
      {
        path: 'orders/order-forecasts',
        name: 'OrderForecasts',
        component: () => import('@/views/orders/OrderForecasts.vue'),
        meta: {
          title: '预告订单',
          requiresAuth: true,
          permissionAny: [
            'orders.sales.view',
            'orders.material_matching.view',
            'orders.dispatch.view',
            'orders.delivery.view'
          ]
        }
      },
      {
        path: 'orders/update-requests',
        name: 'OrderUpdateRequests',
        component: () => import('@/views/orders/OrderUpdateRequests.vue'),
        meta: { title: '订单更新请求管理', requiresAuth: true }
      },
      {
        path: 'orders/dispatch-order',
        name: 'DispatchOrderManagement',
        // 同步打包，避免生产环境异步 chunk 404 / 缓存旧入口导致 ChunkLoadError（桌面派工单打不开）
        component: DispatchOrderManagement,
        meta: { title: '派工单管理', requiresAuth: true, permission: 'orders.dispatch.view' }
      },
      {
        path: 'orders/processing-orders',
        redirect: '/purchase/processing-orders'
      },
      {
        path: 'orders/delivery-orders',
        name: 'DeliveryOrders',
        component: () => import('@/views/orders/DeliveryOrders.vue'),
        meta: { title: '出库单管理', requiresAuth: true, permission: 'orders.delivery.view' }
      },
      {
        path: 'orders/customer-returns',
        name: 'CustomerReturns',
        component: () => import('@/views/orders/CustomerReturns.vue'),
        meta: {
          title: '客户退货',
          requiresAuth: true,
          permissionAny: ['orders.customer_return.view', 'orders.delivery.view']
        }
      },
      {
        path: 'orders/delivery-unit-price-sync',
        name: 'DeliveryUnitPriceSyncRequests',
        component: () => import('@/views/orders/DeliveryUnitPriceSyncRequests.vue'),
        meta: {
          title: '送货单价同步审批',
          requiresAuth: true,
          permissionAny: ['orders.delivery.sync_price.review', 'orders.delivery.sync_price.request']
        }
      },
      {
        path: 'orders/packing-lists',
        name: 'PackingListManagement',
        component: () => import('@/views/orders/PackingListManagement.vue'),
        meta: { title: '包装资料管理', requiresAuth: true, permission: 'orders.delivery.view' }
      },
      {
        path: 'orders/packing-lists/:batchId',
        name: 'PackingListDetail',
        component: () => import('@/views/orders/PackingListDetail.vue'),
        meta: { title: '包装资料明细', requiresAuth: true, permission: 'orders.delivery.view' }
      },
      {
        path: 'orders/color-confirmation',
        name: 'ColorConfirmationManagement',
        component: () => import('@/views/orders/ColorConfirmationManagement.vue'),
        meta: { title: '颜色确认登记', requiresAuth: true, permission: 'orders.color_confirmation.view' }
      },
      {
        path: 'order-types',
        name: 'OrderTypes',
        component: () => import('@/views/OrderTypes.vue'),
        meta: { title: '订单类型管理', requiresAuth: true, permission: 'masterdata.order_types.view' }
      },
      {
        path: 'seasons',
        name: 'Seasons',
        component: () => import('@/views/Seasons.vue'),
        meta: { title: '季节管理', requiresAuth: true, permission: 'masterdata.seasons.view' }
      },
      // 单价管理路由
      {
        path: 'pricing/material-prices',
        name: 'MaterialPriceManagement',
        component: () => import('@/views/pricing/MaterialPriceManagement.vue'),
        meta: { title: '单价管理', requiresAuth: true, permission: 'pricing.view' }
      },
      {
        path: 'units',
        name: 'Units',
        component: () => import('@/views/UnitManagement.vue'),
        meta: { title: '单位管理', requiresAuth: true, permission: 'masterdata.units.view' }
      },
      {
        path: 'unit-conversions',
        name: 'UnitConversions',
        component: () => import('@/views/UnitConversions.vue'),
        meta: { title: '单位换算规则', requiresAuth: true, permission: 'masterdata.unit_conversions.view' }
      },
      {
        path: 'numbering-rules',
        name: 'NumberingRules',
        component: () => import('@/views/NumberingRules.vue'),
        meta: { title: '编码规则管理', requiresAuth: true, permission: 'masterdata.numbering_rules.view' }
      },
      {
        path: 'tax-classifications',
        name: 'TaxClassifications',
        component: () => import('@/views/TaxClassifications.vue'),
        meta: { title: '税务分类管理', requiresAuth: true, permission: 'masterdata.tax_classifications.view' }
      },
      {
        path: 'purchase/orders',
        name: 'PurchaseOrders',
        component: () => import('@/views/purchase/PurchaseOrders.vue'),
        meta: { title: '采购订单管理', requiresAuth: true, permission: 'purchase.orders.view' }
      },
      {
        path: 'purchase/orders/tracking',
        name: 'PurchaseOrderItemsTracking',
        component: () => import('@/views/purchase/PurchaseOrderItemsTracking.vue'),
        meta: { title: '采购订单明细追踪', requiresAuth: true, permission: 'purchase.tracking.view' }
      },
      {
        path: 'purchase/returns',
        name: 'PurchaseReturns',
        component: () => import('@/views/purchase/PurchaseReturns.vue'),
        meta: { title: '采购退货', requiresAuth: true, permission: 'purchase.return.view' }
      },
      {
        path: 'purchase/processing-orders',
        name: 'PurchaseProcessingOrders',
        component: () => import('@/views/orders/ProcessingOrders.vue'),
        meta: { title: '加工单管理', requiresAuth: true, permission: 'orders.material_matching.view' }
      },
      {
        path: 'purchase/processing-follow-up',
        name: 'PurchaseProcessingFollowUp',
        component: () => import('@/views/purchase/ProcessingFollowUp.vue'),
        meta: {
          title: '加工跟进',
          requiresAuth: true,
          permissionAny: ['purchase.processing_follow.view', 'orders.material_matching.view']
        }
      },
      {
        path: 'purchase/market-procurement-follow-up',
        name: 'PurchaseMarketProcurementFollowUp',
        component: () => import('@/views/purchase/MarketProcurementFollowUp.vue'),
        meta: {
          title: '市场采购跟进',
          requiresAuth: true,
          permissionAny: ['purchase.market_procurement.view', 'orders.material_matching.view']
        }
      },
      {
        path: 'purchase/color-formulation-follow-up',
        name: 'PurchaseColorFormulationFollowUp',
        component: () => import('@/views/purchase/ColorFormulationFollowUp.vue'),
        meta: {
          title: '配色跟进',
          requiresAuth: true,
          permissionAny: ['purchase.color_formulation.view', 'orders.material_matching.view']
        }
      },
      {
        path: 'purchase/material-processing-relations',
        name: 'PurchaseMaterialProcessingRelations',
        component: () => import('@/views/inventory/MaterialProcessingRelations.vue'),
        meta: { title: '材料加工关系', requiresAuth: true, permission: 'inventory.material_batches.view' }
      },
      // 库存模块路由
      {
        path: 'inventory/storage-locations',
        name: 'StorageLocations',
        component: () => import('@/views/inventory/StorageLocations.vue'),
        meta: { title: '库位管理', requiresAuth: true, permission: 'inventory.locations.view' }
      },
      {
        path: 'inventory/warehouse-layout',
        name: 'WarehouseLayout',
        // 同步导入，避免生产环境异步 chunk（css/js）MIME 或缓存不一致导致 ChunkLoadError
        component: WarehouseLayout,
        meta: { title: '仓库平面视图', requiresAuth: true, permission: 'inventory.locations.view' }
      },
      {
        path: 'inventory/material-identities',
        name: 'MaterialIdentities',
        component: () => import('@/views/inventory/MaterialIdentities.vue'),
        meta: { title: '材料身份管理', requiresAuth: true, permission: 'inventory.material_batches.view' }
      },
      // 预分配料号系统 - 料号管理
      {
        path: 'inventory/material-batches',
        name: 'MaterialBatches',
        component: () => import('@/views/inventory/MaterialBatches.vue'),
        meta: { title: '料号管理', requiresAuth: true, permission: 'inventory.material_batches.view' }
      },
      {
        path: 'inventory/material-inventory',
        name: 'MaterialInventory',
        component: () => import('@/views/inventory/MaterialInventory/Index.vue'),
        meta: { title: '材料库存管理', requiresAuth: true, permission: 'inventory.records.view' }
      },
      {
        path: 'inventory/dispatch-virtual-balance',
        name: 'DispatchVirtualBalanceMonitor',
        component: () => import('@/views/inventory/DispatchVirtualBalanceMonitor.vue'),
        meta: { title: '派工完成虚库核对', requiresAuth: true, permission: 'inventory.records.view' }
      },
      {
        path: 'inventory/manual-code-intake-requests',
        name: 'InventoryManualCodeIntakeRequests',
        component: () => import('@/views/inventory/InventoryManualCodeIntakeRequests.vue'),
        meta: { title: '盘点未知料号入库申请', requiresAuth: true, permission: 'inventory.records.view' }
      },
      {
        path: 'inventory/operation-approval-requests',
        name: 'InventoryOperationApprovalRequests',
        component: () => import('@/views/inventory/InventoryOperationApprovalRequests.vue'),
        meta: {
          title: '库存出入库审批',
          requiresAuth: true,
          permissionAny: [
            'inventory.operation_approval.review',
            'inventory.operation_approval.initial_review',
            'inventory.operations.edit',
            'inventory.records.edit',
            'global.admin'
          ]
        }
      },
      // 修复库存交易记录路由路径
      {
        path: 'inventory/inventory-transactions',
        name: 'InventoryTransactions',
        component: () => import('@/views/inventory/InventoryTransactions/Index.vue'),
        meta: { title: '库存交易记录', requiresAuth: true, permission: 'inventory.operations.view' }
      },
      // 添加库存操作中心路由
      {
        path: 'inventory/operations',
        name: 'InventoryOperations',
        component: () => import('@/views/inventory/InventoryOperations/Index.vue'),
        meta: { title: '库存操作中心', requiresAuth: true, permission: 'inventory.operations.edit' }
      },
      {
        path: 'inventory/transaction-types',
        name: 'TransactionTypes',
        component: () => import('@/views/inventory/TransactionTypes/Index.vue'),
        meta: { title: '交易类型管理', requiresAuth: true, permission: 'inventory.operations.view' }
      },
      {
        path: 'reports',
        name: 'ReportsHome',
        component: () => import('@/views/reports/ReportsHome.vue'),
        meta: {
          title: '报表中心',
          requiresAuth: true,
          permissionAny: [
            'reports.view',
            'reports.gain_loss.view',
            'inventory.records.view',
            'inventory.operations.view',
            'global.admin',
          ],
        },
      },
      {
        path: 'reports/gain-loss',
        name: 'GainLossReconciliation',
        component: () => import('@/views/reports/GainLossReconciliation.vue'),
        meta: {
          title: '报损报溢核对',
          requiresAuth: true,
          permissionAny: [
            'reports.gain_loss.view',
            'reports.view',
            'inventory.records.view',
            'inventory.operations.view',
            'global.admin',
          ],
        },
      },
      {
        path: 'reports/color-card',
        name: 'ColorCardReport',
        component: () => import('@/views/reports/ColorCardReport.vue'),
        meta: {
          title: '色卡报表',
          requiresAuth: true,
          permissionAny: [
            'reports.gain_loss.view',
            'reports.view',
            'inventory.records.view',
            'inventory.operations.view',
            'global.admin',
          ],
        },
      },
      {
        path: 'reports/stock-take-unstamped',
        name: 'StockTakeUnstampedReport',
        component: () => import('@/views/reports/StockTakeUnstampedReport.vue'),
        meta: {
          title: '盘点未盖章',
          requiresAuth: true,
          permissionAny: [
            'reports.gain_loss.view',
            'reports.view',
            'inventory.records.view',
            'inventory.operations.view',
            'global.admin',
          ],
        },
      },
      {
        path: 'reports/inventory-by-category',
        name: 'InventoryCategoryValueReport',
        component: () => import('@/views/reports/InventoryCategoryValueReport.vue'),
        meta: {
          title: '品名库存货值',
          requiresAuth: true,
          permissionAny: [
            'reports.gain_loss.view',
            'reports.view',
            'inventory.records.view',
            'inventory.operations.view',
            'global.admin',
          ],
        },
      },
      {
        path: 'reports/brand-season',
        name: 'BrandSeasonReport',
        component: () => import('@/views/reports/BrandSeasonReport.vue'),
        meta: {
          title: '品牌季度报表',
          requiresAuth: true,
          permissionAny: [
            'reports.view',
            'reports.gain_loss.view',
            'inventory.records.view',
            'inventory.operations.view',
            'global.admin',
          ],
        },
      },
      {
        path: 'reports/gross-profit',
        name: 'GrossProfitReport',
        component: () => import('@/views/reports/GrossProfitReport.vue'),
        meta: {
          title: '毛利润报表',
          requiresAuth: true,
          adminOnly: true,
        },
      },
      {
        path: 'inventory/material-processing-relations',
        redirect: '/purchase/material-processing-relations'
      },
      // 材料管理路由
      {
        path: 'materials',
        name: 'Materials',
        component: () => import('@/views/Materials.vue'),
        meta: { title: '材料管理', requiresAuth: true, permission: 'masterdata.materials.view' }
      },
      {
        path: 'material-customer-names',
        name: 'MaterialCustomerNames',
        component: () => import('@/views/MaterialCustomerNames.vue'),
        meta: { title: '客户材料名称管理', requiresAuth: true, permission: 'masterdata.material_customer_names.view' }
      },
      // 其他产品库存明细表路由
      {
        path: 'products',
        name: 'OtherProductInventory',
        component: () => import('@/views/OtherProductInventory.vue'),
        meta: { title: '其他产品库存明细表', requiresAuth: true }
      },
      {
        path: 'quality/test-reports',
        name: 'TestReports',
        component: () => import('@/views/quality/TestReports.vue'),
        meta: { title: '测试报告', requiresAuth: true, permission: 'quality.test_reports.view' }
      },
      // 用户权限管理路由（仅管理员）
      {
        path: 'permissions',
        name: 'Permissions',
        component: PermissionsModule,
        meta: { title: '用户权限管理', requiresAuth: true, adminOnly: true }
      },
      // 打印模板管理路由
      {
        path: 'print-templates',
        name: 'PrintTemplateManagement',
        component: () => import('@/views/PrintTemplateManagement.vue'),
        meta: { title: '打印模板管理', requiresAuth: true, permission: 'masterdata.print_templates.view' }
      },
      {
        path: 'specification-templates',
        name: 'SpecificationTemplates',
        component: () => import('@/views/SpecificationTemplates.vue'),
        meta: { title: '规格主数据', requiresAuth: true, permission: 'masterdata.specifications.view' }
      },
      {
        path: 'finance/reconciliation',
        name: 'FinanceReconciliationDashboard',
        component: () => import('@/views/finance/ReconciliationDashboard.vue'),
        meta: { title: '应收应付看板', requiresAuth: true, permission: 'finance.reconciliation.view' }
      },
      {
        path: 'finance/my-receivables',
        name: 'FinanceMyReceivables',
        component: () => import('@/views/finance/ReconciliationDashboard.vue'),
        meta: {
          title: '我的客户应收',
          requiresAuth: true,
          receivableOnly: true,
          permission: 'finance.receivables.view'
        }
      },
      {
        path: 'finance/overview',
        name: 'FinanceOverview',
        component: () => import('@/views/finance/FinanceOverview.vue'),
        meta: { title: '财务工作台', requiresAuth: true, permission: 'finance.overview.view' }
      },
      {
        path: 'finance/monthly-report',
        name: 'FinanceMonthlyReport',
        component: () => import('@/views/finance/FinanceMonthlyReport.vue'),
        meta: { title: '财务月报', requiresAuth: true, permission: 'finance.monthly_report.view' }
      },
      {
        path: 'finance/statement-variance',
        name: 'FinanceStatementVariance',
        component: () => import('@/views/finance/FinanceStatementVariance.vue'),
        meta: { title: '账单与财务差异', requiresAuth: true, permission: 'finance.statement_variance.view' }
      },
      {
        path: 'finance/partner-balances',
        name: 'FinancePartnerBalances',
        component: () => import('@/views/finance/PartnerBalancePage.vue'),
        meta: { title: '往来单位应收应付', requiresAuth: true, permission: 'finance.partner_balances.view' }
      },
      {
        path: 'finance/partner-ledger',
        name: 'FinancePartnerLedger',
        component: () => import('@/views/finance/FinancePartnerLedger.vue'),
        meta: { title: '往来明细账', requiresAuth: true, permission: 'finance.partner_ledger.view' }
      },
      {
        path: 'finance/documents/receipt',
        name: 'FinanceReceiptList',
        component: () => import('@/views/finance/FinanceDocumentList.vue'),
        meta: { title: '收款单', requiresAuth: true, docType: 'receipt', permission: 'finance.documents.receipt.view' }
      },
      {
        path: 'finance/documents/all',
        name: 'FinanceDocumentAllList',
        component: () => import('@/views/finance/FinanceDocumentList.vue'),
        meta: { title: '全部财务单据', requiresAuth: true, allowAllTypes: true, permission: 'finance.documents.view' }
      },
      {
        path: 'finance/documents/payment',
        name: 'FinancePaymentList',
        component: () => import('@/views/finance/FinanceDocumentList.vue'),
        meta: { title: '付款单', requiresAuth: true, docType: 'payment', permission: 'finance.documents.payment.view' }
      },
      {
        path: 'finance/documents/expense',
        name: 'FinanceExpenseList',
        component: () => import('@/views/finance/FinanceDocumentList.vue'),
        meta: { title: '费用单', requiresAuth: true, docType: 'expense', permission: 'finance.documents.expense.view' }
      },
      {
        path: 'finance/documents/advance-receipt',
        name: 'FinanceAdvanceReceiptList',
        component: () => import('@/views/finance/FinanceDocumentList.vue'),
        meta: { title: '预收款单', requiresAuth: true, docType: 'advance_receipt', permission: 'finance.documents.advance_receipt.view' }
      },
      {
        path: 'finance/documents/advance-payment',
        name: 'FinanceAdvancePaymentList',
        component: () => import('@/views/finance/FinanceDocumentList.vue'),
        meta: { title: '预付款单', requiresAuth: true, docType: 'advance_payment', permission: 'finance.documents.advance_payment.view' }
      },
      {
        path: 'finance/documents/other-income',
        name: 'FinanceOtherIncomeList',
        component: () => import('@/views/finance/FinanceDocumentList.vue'),
        meta: { title: '其它收入单', requiresAuth: true, docType: 'other_income', permission: 'finance.documents.other_income.view' }
      },
      {
        path: 'finance/documents/cash-transfer',
        name: 'FinanceCashTransferList',
        component: () => import('@/views/finance/FinanceDocumentList.vue'),
        meta: { title: '提现·存现·转账', requiresAuth: true, docType: 'cash_transfer', permission: 'finance.documents.cash_transfer.view' }
      },
      {
        path: 'finance/vouchers',
        name: 'FinanceVoucherList',
        component: () => import('@/views/finance/FinanceVoucherList.vue'),
        meta: { title: '会计凭证', requiresAuth: true, permission: 'finance.vouchers.view' }
      },
      {
        path: 'finance/accounts',
        name: 'FinanceAccountSettings',
        component: () => import('@/views/finance/FinanceAccountSettings.vue'),
        meta: { title: '会计科目配置', requiresAuth: true, permission: 'finance.accounts.view' }
      },
      {
        path: 'finance/expense-items',
        name: 'FinanceExpenseItemSettings',
        component: () => import('@/views/finance/FinanceExpenseItemSettings.vue'),
        meta: { title: '报销费用科目', requiresAuth: true, permission: 'finance.expense_items.view' }
      },
      {
        path: 'finance/reimbursement-settings',
        name: 'FinanceReimbursementSettings',
        component: () => import('@/views/finance/FinanceReimbursementSettings.vue'),
        meta: { title: '报账规则设置', requiresAuth: true, permission: 'finance.reimbursement_settings.view' }
      },
      {
        path: 'finance/account-balances',
        name: 'FinanceAccountBalance',
        component: () => import('@/views/finance/FinanceAccountBalance.vue'),
        meta: { title: '科目余额表', requiresAuth: true, permission: 'finance.account_balances.view' }
      },
      {
        path: 'finance/cash-reimbursements',
        name: 'FinanceCashReimbursementReview',
        component: () => import('@/views/finance/CashReimbursementReview.vue'),
        meta: { title: '现金报账审核', requiresAuth: true, permission: 'finance.cash_reimbursement.view' }
      },
      {
        path: 'billing/document-center',
        name: 'DocumentCenter',
        component: () => import('@/views/billing/DocumentCenter.vue'),
        meta: { title: '单据中心', requiresAuth: true, permission: 'billing.document_center.view' }
      },
      {
        path: 'billing/prepayment',
        name: 'PrepaymentBilling',
        component: () => import('@/views/billing/StatementManagement.vue'),
        meta: {
          title: '预付请款',
          requiresAuth: true,
          permissionAny: ['billing.prepayment.view', 'billing.statements.view'],
          billingScope: 'prepayment',
          statementView: 'list'
        }
      },
      {
        path: 'billing/prepayment/generate-lines',
        name: 'PrepaymentStatementLineGenerate',
        component: () => import('@/views/billing/StatementLineGenerate.vue'),
        meta: {
          title: '拉取预付请款明细',
          requiresAuth: true,
          permissionAny: ['billing.prepayment.edit', 'billing.statements.edit'],
          billingScope: 'prepayment'
        }
      },
      {
        path: 'billing/statements',
        redirect: () => {
          // 默认进有权限的一侧
          return '/billing/statements/receivable'
        }
      },
      {
        path: 'billing/statements/:kind',
        name: 'BillingStatements',
        component: () => import('@/views/billing/StatementManagement.vue'),
        meta: {
          title: '账单管理',
          requiresAuth: true,
          permissionAny: [
            'billing.statements.view',
            'billing.statements.receivable.view',
            'billing.statements.payable.view'
          ],
          statementView: 'list'
        }
      },
      {
        path: 'billing/statements/:kind/generate-lines',
        name: 'StatementLineGenerate',
        component: () => import('@/views/billing/StatementLineGenerate.vue'),
        meta: {
          title: '生成账单明细',
          requiresAuth: true,
          permissionAny: [
            'billing.statements.edit',
            'billing.statements.receivable.edit',
            'billing.statements.payable.edit'
          ]
        }
      },
      {
        path: 'billing/statements/:kind/reconcile-lines',
        name: 'StatementLinesReconcile',
        component: () => import('@/views/billing/StatementLinesReconcilePage.vue'),
        meta: {
          title: '明细核对',
          requiresAuth: true,
          permissionAny: [
            'billing.statements.view',
            'billing.statements.receivable.view',
            'billing.statements.payable.view',
            'billing.statements.edit',
            'billing.statements.receivable.edit',
            'billing.statements.payable.edit'
          ]
        }
      },
      {
        path: 'billing/reconciliation/:kind',
        name: 'BillingReconciliation',
        redirect: (to) => ({
          path: '/finance/reconciliation',
          query: { tab: to.params.kind === 'payable' ? 'payable' : 'receivable' }
        })
      },
      {
        path: 'billing/payment-footer-accounts',
        name: 'PaymentFooterAccountsManagement',
        component: () => import('@/views/billing/PaymentFooterAccountsManagement.vue'),
        meta: {
          title: '收款账户库',
          requiresAuth: true,
          permissionAny: ['billing.payment_footer_accounts.view', 'billing.statements.view']
        }
      },
      {
        path: 'billing/tax-invoices/vendors',
        name: 'TaxInvoiceVendors',
        component: () => import('@/views/billing/TaxInvoiceParties.vue'),
        meta: {
          title: '开票厂商',
          requiresAuth: true,
          permissionAny: [
            'billing.tax_invoices.view',
            'billing.tax_invoices.edit',
            'billing.tax_invoices.review'
          ],
          partyType: 'vendor'
        }
      },
      {
        path: 'billing/tax-invoices/customers',
        name: 'TaxInvoiceCustomers',
        component: () => import('@/views/billing/TaxInvoiceParties.vue'),
        meta: {
          title: '开票客户',
          requiresAuth: true,
          permissionAny: [
            'billing.tax_invoices.view',
            'billing.tax_invoices.edit',
            'billing.tax_invoices.review'
          ],
          partyType: 'customer'
        }
      },
      {
        path: 'billing/tax-invoices/products',
        name: 'TaxInvoiceProducts',
        component: () => import('@/views/billing/TaxInvoiceProducts.vue'),
        meta: {
          title: '税票品名',
          requiresAuth: true,
          permissionAny: [
            'billing.tax_invoices.view',
            'billing.tax_invoices.edit',
            'billing.tax_invoices.review'
          ]
        }
      },
      {
        path: 'billing/tax-invoices/inbound',
        name: 'TaxInvoicesInbound',
        component: () => import('@/views/billing/TaxInvoiceList.vue'),
        meta: {
          title: '进项发票',
          requiresAuth: true,
          permissionAny: [
            'billing.tax_invoices.view',
            'billing.tax_invoices.edit',
            'billing.tax_invoices.review'
          ],
          taxDirection: 'inbound'
        }
      },
      {
        path: 'billing/tax-invoices/outbound',
        name: 'TaxInvoicesOutbound',
        component: () => import('@/views/billing/TaxInvoiceList.vue'),
        meta: {
          title: '销项发票',
          requiresAuth: true,
          permissionAny: [
            'billing.tax_invoices.view',
            'billing.tax_invoices.edit',
            'billing.tax_invoices.review'
          ],
          taxDirection: 'outbound'
        }
      },
      {
        path: 'billing/tax-invoices/matches',
        name: 'TaxInvoiceMatches',
        component: () => import('@/views/billing/TaxInvoiceMatches.vue'),
        meta: {
          title: '税票核销',
          requiresAuth: true,
          permissionAny: [
            'billing.tax_invoices.view',
            'billing.tax_invoices.edit',
            'billing.tax_invoices.review'
          ]
        }
      },
      {
        path: 'billing/tax-invoices/inventory',
        name: 'TaxInvoiceInventory',
        component: () => import('@/views/billing/TaxInvoiceInventory.vue'),
        meta: {
          title: '税票库存台账',
          requiresAuth: true,
          permissionAny: [
            'billing.tax_invoices.view',
            'billing.tax_invoices.edit',
            'billing.tax_invoices.review'
          ]
        }
      },
      {
        path: 'billing/tax-invoices/yearly-stock',
        name: 'TaxInvoiceYearlyStock',
        component: () => import('@/views/billing/TaxInvoiceYearlyStock.vue'),
        meta: {
          title: '税票品名年度库存',
          requiresAuth: true,
          permissionAny: [
            'billing.tax_invoices.view',
            'billing.tax_invoices.edit',
            'billing.tax_invoices.review'
          ]
        }
      },
      {
        path: 'billing/tax-invoices/item-merge-groups',
        name: 'TaxInvoiceItemMergeGroups',
        component: () => import('@/views/billing/TaxInvoiceItemMergeGroups.vue'),
        meta: {
          title: '品名合并设定',
          requiresAuth: true,
          permissionAny: [
            'billing.tax_invoices.view',
            'billing.tax_invoices.edit',
            'billing.tax_invoices.review'
          ]
        }
      },
      {
        path: 'billing/tax-invoices/outbound-gaps',
        name: 'TaxInvoiceGaps',
        component: () => import('@/views/billing/TaxInvoiceGaps.vue'),
        meta: {
          title: '销项配齐跟进',
          requiresAuth: true,
          permissionAny: [
            'billing.tax_invoices.view',
            'billing.tax_invoices.edit',
            'billing.tax_invoices.review'
          ]
        }
      },
      {
        path: 'billing/tax-invoices/report',
        name: 'TaxInvoiceReport',
        component: () => import('@/views/billing/TaxInvoiceReport.vue'),
        meta: {
          title: '税票期间汇总',
          requiresAuth: true,
          permissionAny: [
            'billing.tax_invoices.view',
            'billing.tax_invoices.edit',
            'billing.tax_invoices.review'
          ]
        }
      },
      {
        path: 'desktop-mobile',
        component: () => import('@/views/mobile/MobileLayout.vue'),
        meta: {
          title: '移动工作台',
          requiresAuth: true,
          embeddedInDesktop: true
        },
        children: getMobileWorkbenchChildren({ basePath: '/desktop-mobile', namePrefix: 'Dm' })
      },
      {
        path: 'tasks',
        component: () => import('@/views/tasks/TaskCenterLayout.vue'),
        meta: { title: '任务中心', requiresAuth: true, permission: 'tasks.view' },
        children: [
          {
            path: '',
            name: 'TaskCenter',
            component: () => import('@/views/tasks/TaskCenter.vue'),
            meta: { title: '任务中心', requiresAuth: true, permission: 'tasks.view' }
          },
          {
            path: 'user/:userId',
            name: 'TaskCenterUserTypes',
            component: () => import('@/views/tasks/TaskCenterUserTypes.vue'),
            meta: { title: '任务分类', requiresAuth: true, permission: 'tasks.view' }
          },
          {
            path: 'user/:userId/:categoryKey',
            name: 'TaskCenterCategory',
            component: () => import('@/views/tasks/TaskCenterCategoryList.vue'),
            meta: { title: '任务列表', requiresAuth: true, permission: 'tasks.view' }
          },
          {
            path: 'summaries',
            name: 'TaskCenterSummaries',
            component: () => import('@/views/tasks/TaskCenterSummaries.vue'),
            meta: { title: '任务摘要历史', requiresAuth: true, permission: 'tasks.view' }
          },
          {
            path: 'manual/:manualTaskId',
            name: 'TaskCenterManualTask',
            component: () => import('@/views/tasks/ManualTaskDetail.vue'),
            meta: { title: '临时任务', requiresAuth: true, permission: 'tasks.view' }
          }
        ]
      },
      {
        path: 'bills',
        redirect: '/billing/statements/receivable'
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

function routeRequiresAuth(to) {
  return to.matched.some((record) => record.meta?.requiresAuth)
}

/** 叶子路由 meta：任一 permissionAny 或单个 permission */
function userAllowsLeafRouteMeta(user, to) {
  const meta = to.matched[to.matched.length - 1]?.meta || {}
  const path = to.path || ''
  const isFinanceRoute = path.startsWith('/finance/') || path.startsWith('/billing/')
  const checkPermission = (p) => (isFinanceRoute ? canAccessFinanceModuleKey(user, p) : hasPermission(user, p))
  const anyList = meta.permissionAny
  if (Array.isArray(anyList) && anyList.length) {
    return anyList.some((p) => checkPermission(p))
  }
  if (meta.permission) {
    return checkPermission(meta.permission)
  }
  return true
}

// 路由守卫
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()
  if (shouldLogoutForIdle()) {
    userStore.clearUser()
    if (to.path !== '/login') {
      await forceAuthRelogin('长时间未操作，请重新登录')
      return
    }
  }
  const token = localStorage.getItem('token')

  // 进入登录页 / 移动工作台时摘掉可能残留的全屏 Loading、body 滚动锁
  if (to.path === '/login' || to.path.startsWith('/m') || to.path.startsWith('/desktop-mobile')) {
    resetBodyElLoadingArtifacts()
  }

  if (routeRequiresAuth(to) && !token) {
    next({ path: '/login', query: { redirect: to.fullPath }, replace: true })
    return
  }

  if (token && to.path !== '/login') {
    markUserActive()
    userStore.initFromStorage()
    // 仅首屏 await 拉权限；切页不阻塞（原先带 permission 的页面每次 force 同步 verify，全站切页都卡）
    const isColdStart = from.matched.length === 0
    if (isColdStart) {
      await userStore.syncUserFromServer({ force: true })
    } else {
      void userStore.syncUserFromServer({ force: false })
    }
    if (routeRequiresAuth(to) && !localStorage.getItem('token')) {
      next({ path: '/login', query: { redirect: to.fullPath }, replace: true })
      return
    }
  }

  const user = userStore.user

  // 首次登录强制改密（放过登录页、改密页）
  if (
    token &&
    user?.must_change_password &&
    to.path !== '/change-password' &&
    to.path !== '/login'
  ) {
    next({ path: '/change-password' })
    return
  }

  const normalizedPath = to.path.replace(/\/$/, '') || '/'

  if (
    token &&
    user &&
    normalizedPath === '/m/home' &&
    getVisibleMobileNavItems(user).length
  ) {
    next({ path: getFirstAllowedMobilePath(user), replace: true })
    return
  }

  // 仅首屏/仪表盘自动进移动工作台；桌面 ERP 模块（含 /tasks 分层）不劫持，避免任务中心误入 /m 无法回到顶栏菜单
  const mobileAutoRedirectRoots = new Set(['/', '/dashboard'])
  if (
    token &&
    user &&
    isLikelyMobileBrowser() &&
    !to.path.startsWith('/m') &&
    to.path !== '/login' &&
    to.path !== '/change-password' &&
    mobileAutoRedirectRoots.has(normalizedPath)
  ) {
    const dest = getFirstAllowedMobilePath(user)
    if (normalizedPath !== dest.replace(/\/$/, '')) {
      next({ path: dest, replace: true })
      return
    }
  }

  if (to.path.startsWith(MOBILE_DESKTOP_EMBED_BASE)) {
    if (!canAccessDesktopMobileWorkbench(user)) {
      ElMessage.warning('你没有访问移动工作台的权限')
      next({ path: '/dashboard' })
      return
    }
    if (!canAccessMobilePath(to.path, user)) {
      next({ path: getFirstAllowedMobilePath(user, MOBILE_DESKTOP_EMBED_BASE), replace: true })
      return
    }
  }

  // user 未就绪时勿做权限跳转，否则独立窗口冷启动易死循环白屏
  if (user && to.path.startsWith('/m') && !canAccessMobilePath(to.path, user)) {
    ElMessage.warning('你没有访问该页面的权限')
    next({ path: getFirstAllowedMobilePath(user), replace: true })
    return
  }

  const leafMeta = to.matched[to.matched.length - 1]?.meta
  if (leafMeta?.adminOnly) {
    if (!canManageUsersAndRoles(user)) {
      ElMessage.warning('仅管理员可访问用户权限管理')
      next({ path: '/dashboard', replace: true })
      return
    }
  }
  if (leafMeta?.permission || leafMeta?.permissionAny) {
    if (!userAllowsLeafRouteMeta(user, to)) {
      ElMessage.warning('你没有访问该页面的权限')
      if (to.path.startsWith('/m')) {
        next({ path: getFirstAllowedMobilePath(user), replace: true })
      } else if (isLikelyMobileBrowser()) {
        next({ path: getFirstAllowedMobilePath(user), replace: true })
      } else {
        next({ path: '/dashboard' })
      }
      return
    }
  }

  // 账单应收/应付分权限：有列表权不代表两侧都能进
  if (
    token &&
    user &&
    (to.name === 'BillingStatements' || to.name === 'StatementLineGenerate' || to.name === 'StatementLinesReconcile')
  ) {
    const kind = String(to.params.kind || '').toLowerCase()
    if (kind === 'payable' || kind === 'receivable') {
      if (!canViewBillingStatements(user, kind)) {
        ElMessage.warning(kind === 'payable' ? '无权访问应付账单' : '无权访问应收账单')
        if (canViewBillingStatements(user, kind === 'payable' ? 'receivable' : 'payable')) {
          next({
            path: `/billing/statements/${kind === 'payable' ? 'receivable' : 'payable'}`,
            replace: true
          })
        } else {
          next({ path: '/dashboard', replace: true })
        }
        return
      }
    }
  }

  next()
})

export default router
